import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';

const FORM_URL = 'https://readdy.ai/api/form/d7kdndl0lfokr4m6je10';

const productTypes = [
  'ハードカプセル',
  'ソフトジェル',
  '錠剤',
  'チュアブル錠',
  '粉末・顆粒',
  '機能性飲料',
  '内服液',
  'グミ',
  'ゼリースティック',
  'ティーバッグ',
  'その他',
];

const quantities = [
  '1,000〜3,000個',
  '3,000〜10,000個',
  '10,000〜50,000個',
  '50,000個以上',
  '未定',
];

const formulaOptions = [
  '具体的な処方イメージあり',
  '概念はあるが支援が必要',
  'まだ決まっていない・提案希望',
];

const processSteps = [
  { step: '01', title: 'お問い合わせ送信', desc: '製品の方向性・予定ロット数・剤型・連絡先をご記入ください。', icon: 'ri-file-edit-line' },
  { step: '02', title: '初回ご連絡', desc: '1営業日以内に担当者よりご連絡し、基本的なご要望を確認します。', icon: 'ri-customer-service-2-line' },
  { step: '03', title: 'ニーズの確認・可能性評価', desc: 'ブランドのポジショニング・チャネル・予算・製品目標に合わせて、最適な処方・剤型・包材をご提案します。', icon: 'ri-search-eye-line' },
  { step: '04', title: '初回提案・見積', desc: 'ご要望確認後、初回の提携提案・スケジュール・見積をご提供します。', icon: 'ri-price-tag-3-line' },
];

export default function JaContactFormPage() {
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useSEO({
    title: 'お問い合わせフォーム｜OEM ODM健康食品お問い合わせ｜康永生技',
    description: '康永生技のOEM/ODMお問い合わせフォーム：製品タイプ、予定数量、処方イメージ、ご要望などをご記入ください。日本語対応スタッフが1営業日以内にご返答します。',
    keywords: 'OEM問い合わせフォーム,ODM問い合わせフォーム,健康食品相談,康永お問い合わせフォーム,製造お問い合わせ',
    lang: 'ja',
    canonicalPath: '/ja/contact/form',
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="requirement"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitState('loading');
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitState('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <JaLayout>
      {/* Hero */}
      <section
        className="relative pt-24 pb-16 bg-cover bg-top"
        style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=clean%20white%20natural%20laboratory%20workspace%20with%20soft%20green%20plants%20and%20morning%20light%20streaming%20through%20large%20windows%20minimalist%20aesthetic%20healthcare%20biotech%20professional%20environment%20muted%20tones%20serene%20atmosphere&width=1600&height=560&seq=contact-form-hero&orientation=landscape')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/28 to-black/45" />
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-4 text-white/60 text-[0.8125rem]">
            <Link to="/ja" className="hover:text-white transition-colors cursor-pointer">ホーム</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/ja/contact" className="hover:text-white transition-colors cursor-pointer">お問い合わせ</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/90">お問い合わせフォーム</span>
          </div>
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-[0.8125rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-white/30">
            お問い合わせ
          </span>
          <h1 className="text-white font-bold text-[2.25rem] md:text-[3rem] leading-tight mb-4 max-w-2xl">
            お問い合わせフォーム
          </h1>
          <p className="text-white/85 text-[1.0625rem] leading-[1.8] max-w-xl">
            製品開発のご要望をお知らせください。最適なOEM/ODM製造方法をご提案いたします。
          </p>
          <div className="mt-6 h-0.5 w-14 bg-[#81C784] rounded-full" />
        </div>
      </section>

      {/* Notice bar */}
      <div className="bg-[#e8f5e9] border-y border-[#c8e6c9]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center gap-4">
          {[
            { icon: 'ri-time-line', text: '1営業日以内にご返答' },
            { icon: 'ri-shield-check-line', text: '個人情報保護' },
            { icon: 'ri-customer-service-2-line', text: '日本語対応スタッフ' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5 text-[#388E3C] text-[0.8125rem] font-medium">
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${item.icon} text-sm`} /></div>
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* Process steps */}
      <section className="py-14 bg-[#f7faf7] border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="mb-8">
            <h2 className="text-[#1a2e1a] font-bold text-[1.375rem] md:text-[1.625rem] mb-2">送信後の流れ</h2>
            <p className="text-gray-500 text-[0.9rem] leading-[1.75]">お問い合わせから製品開発まで、明確なフローでご案内します。</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {processSteps.map((s, idx) => (
              <div key={s.step} className="relative bg-white rounded-xl p-5 border border-gray-100">
                {idx < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-[2.25rem] left-full w-5 h-0.5 bg-[#c8e6c9] z-10" />
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#e8f5e9] rounded-full flex-shrink-0 border border-[#c8e6c9]">
                    <i className={`${s.icon} text-[#4CAF50] text-base`} />
                  </div>
                  <span className="text-[#4CAF50] font-bold text-[0.75rem] tracking-widest">STEP {s.step}</span>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main form section */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 xl:gap-16 items-start">

            {/* Sidebar */}
            <div className="w-full lg:w-[300px] xl:w-[340px] flex-shrink-0 space-y-5">
              <div>
                <h2 className="text-[#1a2e1a] font-bold text-[1.25rem] mb-1.5">ご記入の前に</h2>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">以下の情報を参考に、スムーズなお問い合わせにお役立てください。</p>
              </div>
              {[
                { icon: 'ri-flask-line', title: '製品開発のご相談', desc: '新規処方開発・剤型選定・既存処方の改善など、どんな段階でもご相談ください。' },
                { icon: 'ri-leaf-line', title: '天然原料の選定', desc: 'ブランドのポジショニングに合った天然原料の組み合わせ選定をサポートします。' },
                { icon: 'ri-file-shield-2-line', title: '法規・表示確認', desc: '法規リスクと表示方向の確認を同時にサポートし、適切な市場投入を支援します。' },
                { icon: 'ri-box-3-line', title: 'パッケージデザイン', desc: '容器選定からビジュアルデザインまで、パッケージングの一貫したサポートを提供します。' },
              ].map((tip) => (
                <div key={tip.title} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-4 border border-gray-100">
                  <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                    <i className={`${tip.icon} text-[#4CAF50] text-base`} />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-semibold text-[0.875rem] mb-0.5">{tip.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.7]">{tip.desc}</p>
                  </div>
                </div>
              ))}

              <div className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-3">直接ご連絡も歓迎</p>
                <div className="space-y-2.5">
                  <a href="tel:+886422029858" className="flex items-center gap-2.5 text-gray-600 hover:text-[#4CAF50] transition-colors cursor-pointer text-[0.875rem]">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200"><i className="ri-phone-line text-[#4CAF50] text-sm" /></div>
                    +886-4-2202-9858
                  </a>
                  <a href="mailto:natural1070606@gmail.com" className="flex items-center gap-2.5 text-gray-600 hover:text-[#4CAF50] transition-colors cursor-pointer text-[0.875rem] break-all">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0"><i className="ri-mail-line text-[#4CAF50] text-sm" /></div>
                    natural1070606@gmail.com
                  </a>
                </div>
              </div>

              <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-7 h-7 flex items-center justify-center"><i className="ri-line-fill text-[#4CAF50] text-xl" /></div>
                  <p className="text-[#1a2e1a] font-bold text-[0.9375rem]">LINEでチャット相談</p>
                </div>
                <p className="text-gray-600 text-[0.8125rem] leading-[1.7] mb-3">日本語でのチャット相談を歓迎します。LINEで直接お問い合わせいただけます。</p>
                <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-4 py-2.5 rounded-full transition-all duration-300 cursor-pointer w-full">
                  <i className="ri-line-fill" /> LINEに追加
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="w-full lg:flex-1">
              <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] mb-2">ODMお問い合わせフォーム</h2>
              <p className="text-gray-500 text-[0.9375rem] mb-7 leading-[1.75]">以下の項目にご記入ください。担当者より1営業日以内にご連絡いたします。</p>

              {submitState === 'success' ? (
                <div className="flex flex-col items-center text-center bg-[#f7faf7] rounded-2xl border border-green-100 overflow-hidden">
                  <div className="w-full px-8 pt-12 pb-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5 border border-green-200 mx-auto">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-3xl" />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.375rem] mb-3">送信完了しました。ありがとうございます！</h3>
                    <p className="text-gray-500 text-[0.9375rem] leading-[1.75] max-w-sm mx-auto">康永生技より1営業日以内にご連絡し、製品の方向性・剤型・ロット数・提携方法についてご確認します。</p>
                  </div>
                  <div className="w-full bg-[#e8f5e9] border-t border-[#c8e6c9] px-6 sm:px-8 py-6">
                    <p className="text-[#1a2e1a] font-semibold text-[0.9375rem] mb-1">より迅速な対応をご希望の場合</p>
                    <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-4">LINEに追加してチームに直接ご連絡いただけます。</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto">
                        <i className="ri-line-fill text-lg" /> LINEに追加
                      </a>
                      <span className="text-[#4CAF50] text-[0.875rem] font-medium">LINE@：@126uwznp</span>
                    </div>
                  </div>
                  <div className="px-8 py-5">
                    <button onClick={() => setSubmitState('idle')} className="whitespace-nowrap border border-gray-300 text-gray-500 hover:border-[#4CAF50] hover:text-[#4CAF50] font-medium text-[0.875rem] px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer">
                      再度送信する
                    </button>
                  </div>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">お名前 <span className="text-red-500">*</span></label>
                      <input name="contact_name" type="text" required placeholder="お名前をご記入ください" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">会社・ブランド名</label>
                      <input name="company_name" type="text" placeholder="会社名またはブランド名" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">電話番号 <span className="text-red-500">*</span></label>
                      <input name="phone" type="tel" required placeholder="例：+81-3-0000-0000" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">メールアドレス <span className="text-red-500">*</span></label>
                      <input name="email" type="email" required placeholder="your@email.com" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">希望製品タイプ</label>
                    <select name="product_type" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer" defaultValue="">
                      <option value="" disabled>製品タイプを選択</option>
                      {productTypes.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">予定製造数量</label>
                    <select name="quantity" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer" defaultValue="">
                      <option value="" disabled>予定数量を選択</option>
                      {quantities.map((q) => <option key={q} value={q}>{q}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-2">処方イメージ</label>
                    <div className="flex flex-wrap gap-2">
                      {formulaOptions.map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="formula_idea" value={opt} className="accent-[#4CAF50] cursor-pointer" />
                          <span className="text-[#1a2e1a] text-[0.875rem]">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      ご要望・詳細{' '}
                      <span className={`text-[0.75rem] font-normal ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>({charCount}/500)</span>
                    </label>
                    <textarea
                      name="requirement"
                      rows={5}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="製品の方向性、ターゲット、予算、販路、その他ご要望をご記入ください。"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors resize-none bg-white"
                    />
                    {charCount > 500 && <p className="text-red-500 text-[0.75rem] mt-1">500文字以内でご記入ください</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={submitState === 'loading' || charCount > 500}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] disabled:opacity-60 text-white font-bold text-[1rem] py-4 rounded-lg transition-all duration-300 cursor-pointer"
                  >
                    {submitState === 'loading' ? (
                      <><i className="ri-loader-4-line animate-spin text-lg" />送信中…</>
                    ) : (
                      <><i className="ri-send-plane-line text-lg" />送信する</>
                    )}
                  </button>

                  {submitState === 'error' && <p className="text-red-500 text-[0.875rem] text-center">送信に失敗しました。再度お試しいただくか、直接ご連絡ください。</p>}

                  <div className="border-t border-gray-100 pt-5 mt-1">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#f7faf7] rounded-xl px-5 py-4 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-[#e8f5e9] rounded-full border border-[#c8e6c9] flex-shrink-0">
                          <i className="ri-line-fill text-[#4CAF50] text-lg" />
                        </div>
                        <div>
                          <p className="text-[#1a2e1a] font-semibold text-[0.875rem]">より早い返答をご希望の場合</p>
                          <p className="text-gray-500 text-[0.8125rem]">送信後にLINEへ追加を。LINE@：<span className="text-[#4CAF50] font-medium">@126uwznp</span></p>
                        </div>
                      </div>
                      <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 w-full sm:w-auto">
                        <i className="ri-line-fill" /> LINEに追加
                      </a>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* LINE CTA Bottom */}
      <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5 bg-white rounded-2xl border border-[#c8e6c9] px-6 sm:px-8 py-6 sm:py-8 mb-10">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="w-12 h-12 flex items-center justify-center bg-[#e8f5e9] rounded-full border border-[#c8e6c9] flex-shrink-0">
                <i className="ri-line-fill text-[#4CAF50] text-2xl" />
              </div>
              <div className="min-w-0">
                <p className="text-[#1a2e1a] font-bold text-[1rem] mb-0.5">LINEで直接ご相談も歓迎</p>
                <p className="text-gray-500 text-[0.875rem] leading-[1.7]">専任担当者がすぐにご対応します。LINE@：<span className="text-[#4CAF50] font-semibold">@126uwznp</span></p>
              </div>
            </div>
            <a
              href="https://lin.ee/X7Hcpf4"
              rel="nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[0.9375rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 w-full sm:w-auto"
            >
              <i className="ri-line-fill text-lg" />
              LINEに追加
            </a>
          </div>
          <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">関連ページ</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'よくある質問', desc: 'MOQ・処方・法規・リードタイムなど、よくいただく質問をまとめました。', href: '/ja/cooperation/faq' },
              { title: '提携フロー', desc: '最初のご相談から納品まで、提携の各ステップを詳しくご紹介します。', href: '/ja/cooperation/process' },
              { title: '連絡先情報', desc: '電話・メール・会社住所・LINEの連絡先情報。', href: '/ja/contact/info' },
            ].map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer block"
              >
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5">{item.title}</p>
                <p className="text-gray-500 text-[0.875rem] leading-[1.65]">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </JaLayout>
  );
}
